import {type ReactNode, useCallback, useMemo, useState} from 'react'

import {SavedViewConfigurationProvider} from '../../contexts/views/SavedViewConfigurationProvider'
import type {ViewConfiguration, ViewConfigurationOverrides} from '../../contexts/views/view-configuration'
import type {SavedViewBoardLayout} from '../board/issues/IssuesSavedViewBoard'
import {NewViewBoardPreview} from '../board/issues/NewViewBoardPreview'
import {ConnectedSavedViewOptionsMenu} from './ConnectedSavedViewOptionsMenu'

interface NewViewConfiguration {
  configurationFields: ReactNode
  /** Draws the chosen board in place of the form's `list` preview, or none while no board is chosen. */
  renderBoardPreview: ((list: ReactNode) => ReactNode) | undefined
  configuration: ViewConfiguration | undefined
  isDirty: boolean
}

/**
 * Starts at list. A list sends no configuration; a board sends `defaultConfiguration` with the
 * picks made over it. Without `defaultConfiguration` there are no fields.
 */
export function useNewViewConfiguration({
  defaultConfiguration,
  basePath,
  routeId,
  boardLayout,
}: {
  defaultConfiguration: ViewConfiguration | undefined
  basePath: string
  routeId: string
  boardLayout: SavedViewBoardLayout
}): NewViewConfiguration {
  const [changes, setChanges] = useState<ViewConfigurationOverrides>({})
  const applied = useMemo(
    () => defaultConfiguration && {...defaultConfiguration, layout: 'list' as const, ...changes},
    [changes, defaultConfiguration],
  )
  const applyChanges = useCallback(
    (next: ViewConfigurationOverrides) => setChanges(current => ({...current, ...next})),
    [],
  )
  const reset = useCallback(() => setChanges({}), [])
  const configuration = applied?.layout === 'board' ? applied : undefined
  const provide = (configured: ViewConfiguration, children: ReactNode) => (
    <SavedViewConfigurationProvider
      appliedConfiguration={configured}
      onConfigurationChange={applyChanges}
      onReset={reset}
    >
      {children}
    </SavedViewConfigurationProvider>
  )

  return {
    configurationFields: applied
      ? provide(applied, <ConnectedSavedViewOptionsMenu basePath={basePath} restoreBoardPicks />)
      : undefined,
    renderBoardPreview: configuration
      ? list =>
          provide(
            configuration,
            <NewViewBoardPreview basePath={basePath} routeId={routeId} list={list} layout={boardLayout} />,
          )
      : undefined,
    configuration,
    isDirty: configuration !== undefined,
  }
}
